import type { TCPOffsetOption } from '@sb/motion-planning';

import type { LoadedRoutine } from './LoadedRoutine';
import type { RoutineContext } from './RoutineContext';
import type { TaggedRoutineStepVariables } from './Step';
import type Step from './Step/Step';
import type { ArmPosition, Conditional, Expression, Space } from './types';

export interface PalletizingVisualizationOptions {
  scope: 'allBoxes' | 'allBoxesInLayer' | 'specificBox';
  opacity: boolean;
  labels: boolean;
  layerIndex?: number;
  boxIndex?: number;
  palletBaseID?: string;
}

export interface PositionListVisualizationOptions {
  positionScope?: 'allPositions' | 'specificPosition';
  positionIndex?: number;
}

/**
 * Per-call state for one invocation of a routine/subroutine. Holds state that is
 * unique to the subroutine call (as opposed to RoutineContext which holds state and context
 * that applies to the entire loaded routine + reachable subroutines). The loaded
 * routine's frame is the root with `scopeKey: 'root'`; each `CallSubroutine`
 * call allocates a fresh frame with a scope tree that includes lineage from the root.
 */
export class RoutineFrame {
  /**
   * The load session this frame belongs to — identity-pinned at
   * construction, so the frame always resolves against the globals and
   * caches of the load that created it. The frame's scope parent.
   */
  public readonly loadedRoutine: LoadedRoutine;

  /** Process-wide services, reached through the owning session. */
  public get context(): RoutineContext {
    return this.loadedRoutine.context;
  }

  // Structural / identity (per-call, set at construction, never mutated)
  public readonly scopeKey: string;

  public readonly routineId: string;

  public readonly routineName: string;

  public readonly callSubroutineStepId: string | null;

  /**
   * Parent frame in the call chain. `null` for the loaded routine's
   * frame; for each per-call sub frame, the frame the `CallSubroutine`
   * step that invoked it was running on. Enables walking back from a
   * leaf step's frame to the loaded routine to resolve which loaded-
   * routine `CallSubroutine` step started the chain (R11 failure
   * attribution — `failedStepLoadedRoutineStepId`).
   */
  public readonly parentFrame: RoutineFrame | null;

  // Per-call mutable state — kept public for state-emission and snapshot
  // reads, but mutations go through the methods below.
  public variableValues: Record<string, TaggedRoutineStepVariables>;

  public localSpace: Space.Item[];

  /**
   * Top-level step instances of the routine this frame represents.
   * Populated after the routine's step tree is constructed (loaded routine:
   * by `RoutineRunner.loadRoutine`; per-call sub: by
   * `CallSubroutineStep._play`). `Step.getStepPosition` reads this to DFS-
   * walk the tree and find a step's 1-based position for R11 failure
   * attribution.
   */
  public routineSteps: Step[] = [];

  public constructor(args: {
    loadedRoutine: LoadedRoutine;
    scopeKey: string;
    routineId: string;
    routineName: string;
    callSubroutineStepId: string | null;
    parentFrame: RoutineFrame | null;
    initialSpace: Space.Item[];
  }) {
    this.loadedRoutine = args.loadedRoutine;
    this.scopeKey = args.scopeKey;
    this.routineId = args.routineId;
    this.routineName = args.routineName;
    this.callSubroutineStepId = args.callSubroutineStepId;
    this.parentFrame = args.parentFrame;
    this.variableValues = {};
    this.localSpace = args.initialSpace;
  }

  /**
   * Get the variables for a step within this frame.
   *
   * This frame's `variableValues` first, then workspace
   * globals on `RoutineContext` as a fallback. Throws on miss.
   */
  public getVariable(stepID: string): TaggedRoutineStepVariables {
    if (stepID in this.variableValues) {
      return this.variableValues[stepID];
    }

    if (stepID in this.loadedRoutine.globalVariableValues) {
      return this.loadedRoutine.globalVariableValues[stepID];
    }

    throw new Error(
      `Tried to get variable state for step "${stepID}" which either does not exist or failed to set variables`,
    );
  }

  /**
   * Name-based twin of `getVariable` with the same visibility rules:
   * this frame's bag first, then workspace globals — so on a cross-bag
   * name collision the local wins. Matches any variant carrying a
   * user-facing name (environment variables, learned-model outputs).
   * Returns null on miss (names come from user input; a miss is a normal
   * request error, not a programming error like a bad stepID).
   */
  public getVariableByName(
    name: string,
  ): { stepID: string; variable: TaggedRoutineStepVariables } | null {
    for (const bag of [
      this.variableValues,
      this.loadedRoutine.globalVariableValues,
    ]) {
      for (const stepID of Object.keys(bag)) {
        const variable = bag[stepID];

        if ('name' in variable && variable.name === name) {
          return { stepID, variable };
        }
      }
    }

    return null;
  }

  /**
   * Everything visible in this scope as one record: workspace globals with
   * this frame's bag merged over them (same key wins to the frame, matching
   * `getVariable`'s precedence). The loaded routine's wire snapshot is the
   * root frame's snapshot.
   */
  public variablesSnapshot(): Record<string, TaggedRoutineStepVariables> {
    return {
      ...this.loadedRoutine.globalVariableValues,
      ...this.variableValues,
    };
  }

  public setVariable(stepID: string, state: TaggedRoutineStepVariables): void {
    // Only `EnvironmentVariable`-shaped state carries `isGlobal`; the
    // structural `'isGlobal' in state` narrowing handles the union safely.
    if ('isGlobal' in state && state.isGlobal) {
      this.loadedRoutine.globalVariableValues[stepID] = state;
    } else {
      this.variableValues[stepID] = state;
    }

    this.context.notifyVariablesChange(this.loadedRoutine.getAllVariables());
  }

  /**
   * Look up a space item visible to this frame, or null on miss.
   *
   * THE canonical layered lookup: workspace globals first, then this
   * frame's `localSpace`. Global-first because routine documents can carry
   * stale cached copies of global items, and the live global must keep
   * winning that collision. Every merged-space view derives from this rule
   * — via this method for single lookups, via `visibleSpace` for
   * snapshots — so the precedence lives here and nowhere else.
   */
  public findSpaceItem(id: string): Space.Item | null {
    const global = this.loadedRoutine.globalSpace.find((i) => i.id === id);
    if (global) return global;

    return this.localSpace.find((i) => i.id === id) ?? null;
  }

  /**
   * All space items visible to this frame as one array, in
   * lookup-precedence order (workspace globals first, then this frame's
   * locals): `visibleSpace().find(byId)` resolves identically to
   * `findSpaceItem(id)`.
   */
  public visibleSpace(): Space.Item[] {
    return [...this.loadedRoutine.globalSpace, ...this.localSpace];
  }

  /**
   * `findSpaceItem`, throwing on miss — for callers where a missing item
   * is a programming or spec error rather than a normal branch.
   */
  public getSpaceItem(id: string): Space.Item {
    const item = this.findSpaceItem(id);

    if (!item) {
      throw new Error(`Space item with ID ${id} does not exist`);
    }

    return item;
  }

  /**
   * Read one entry of a position-list space item visible to this frame,
   * wrapping the index modulo the list length.
   */
  public getPositionListEntry(
    positionListID: string,
    index: number,
  ): ArmPosition {
    const positionList = this.getSpaceItem(positionListID).positions;

    if (positionList.length === 0) {
      throw new Error(`Position list "${positionListID}" is empty`);
    }

    return positionList[index % positionList.length];
  }

  /**
   * Replace the entries of a position-list space item visible to this
   * frame. Mutates the item in place (a sub-local item stays sub-local;
   * a workspace-global item is shared by all scopes).
   */
  public setPositionListEntries(
    positionListID: string,
    positions: Space.Position[],
  ): void {
    const spaceItem = this.getSpaceItem(positionListID);
    spaceItem.positions = positions;
  }

  /**
   * Show or hide the visualization of a space item visible to this frame.
   * The write is scope-keyed by ownership (a workspace-global item is
   * scope-less; a local item is keyed by this frame's scope); the shared
   * wire plumbing lives on the owning `RoutineContext`.
   */
  public updateSpaceItemVisualization(
    id: string,
    action: 'show' | 'hide',
    palletizing?: PalletizingVisualizationOptions,
    positionList?: PositionListVisualizationOptions,
  ): void {
    this.context._updateSpaceItemVisualizationImpl(
      this,
      id,
      action,
      palletizing,
      positionList,
    );
  }

  /**
   * Show or hide a workpiece visualization for a space item visible to
   * this frame, keyed by this frame's scope. Delegates to the owning
   * `RoutineContext`'s shared implementation.
   */
  public updateWorkpieceVisualization(
    id: string,
    action: 'show' | 'hide',
    visualizationType: 'tooltip' | 'pickup',
    boxTypeID: string,
    tooltipOption?: TCPOffsetOption,
  ): void {
    this.context._updateWorkpieceVisualizationImpl(
      this,
      id,
      action,
      visualizationType,
      boxTypeID,
      tooltipOption,
    );
  }

  /**
   * Evaluate an expression in the scope of this frame.
   *
   * Scoped lookups (`variable` expressions, space snapshots) resolve
   * against this frame, so per-call state is honored. Delegates to the
   * owning context's `ExpressionEvaluator` with this frame as the scope.
   */
  public async evaluateExpression(
    expression: Expression,
    errMsgPrefix?: string,
    noFinalFailAndThrow?: boolean,
  ): Promise<any> {
    return this.context.expressionEvaluator.evaluateExpression(
      expression,
      this,
      errMsgPrefix,
      noFinalFailAndThrow,
    );
  }

  /**
   * Evaluate a conditional expression in the scope of this frame and
   * return the boolean result.
   *
   * Variable operands resolve through this frame (via `evaluateExpression`),
   * so per-call state is honored. Delegates the operator-switch logic to
   * the owning context's `ExpressionEvaluator`.
   */
  public async evaluateConditional(condition: Conditional): Promise<boolean> {
    const { result } =
      await this.context.expressionEvaluator.evaluateConditional(
        condition,
        this,
      );

    return result;
  }

  /**
   * Evaluate a conditional expression in the scope of this frame and
   * return both the boolean result and which OR branch matched (1-indexed).
   * Used by Waypoint steps to track which stop-early condition triggered.
   */
  public async evaluateConditionalWithBranch(
    condition: Conditional,
  ): Promise<{ result: boolean; matchedBranch?: number }> {
    return this.context.expressionEvaluator.evaluateConditional(
      condition,
      this,
    );
  }
}
